import { ListChecks } from 'lucide-react';
import { useState } from 'react';

import { useEscopoEmpresas } from '@/lib/escopo';
import { juntarPaginas } from '@/lib/paginacao';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { TabelaDados } from '@/ui/components/tabela-dados';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { PaginaLista } from '@/ui/patterns/pagina-lista';
import { Input } from '@/ui/primitives/input';
import { type TipoPendencia } from '@fiscoai/contracts';

import { type FiltrosPendencias, usePendencias, useResumoPendencias } from './api';
import { colunasPendencias } from './colunas';
import { IndicadoresPendencias, MesPronto } from './indicadores-pendencias';
import { TIPO_PENDENCIA } from './rotulos';

const OPCOES_TIPO = (Object.keys(TIPO_PENDENCIA) as TipoPendencia[]).map((t) => ({
  valor: t,
  rotulo: TIPO_PENDENCIA[t],
}));

const chave = (p: {
  tipo: string;
  documentoId: string | null;
  chave: string | null;
  empresaId: string | null;
}) => `${p.tipo}-${p.documentoId ?? p.chave ?? p.empresaId ?? ''}`;

export function PaginaPendencias() {
  const escopo = useEscopoEmpresas();
  const [filtros, setFiltros] = useState<FiltrosPendencias>({});
  const definir = <K extends keyof FiltrosPendencias>(k: K, v: FiltrosPendencias[K]) => {
    setFiltros((atual) => limparVazios({ ...atual, [k]: v }));
  };
  const periodo = { emitidaDe: filtros.emitidaDe, emitidaAte: filtros.emitidaAte };
  const resumo = useResumoPendencias(escopo, limparVazios(periodo));
  const lista = usePendencias(escopo, filtros);
  const pendencias = juntarPaginas(lista.data);

  return (
    <PaginaLista
      cabecalho={
        <CabecalhoPagina
          titulo="Pendências"
          descricao="O que falta para fechar o mês: notas sem XML, sem classificação ou com alerta."
        />
      }
      indicadores={
        <IndicadoresPendencias
          resumo={resumo.data}
          ativa={filtros.prioridade}
          aoAlternar={(p) => {
            definir('prioridade', p);
          }}
        />
      }
      filtros={
        <div className="grid gap-3">
          {resumo.data?.porPrioridade.ALTA === 0 && <MesPronto />}
          <div className="flex flex-wrap gap-2">
            <SelectPesquisavel
              aria-label="Tipo de pendência"
              rotuloBusca="Buscar tipo"
              placeholder="Todos os tipos"
              className="w-72"
              opcoes={OPCOES_TIPO}
              valor={filtros.tipo ?? null}
              permitirLimpar
              aoMudar={(v) => {
                definir('tipo', (v ?? undefined) as TipoPendencia | undefined);
              }}
            />
            <Input
              type="date"
              aria-label="Emitidas desde"
              className="w-40"
              value={filtros.emitidaDe ?? ''}
              onChange={(e) => {
                definir('emitidaDe', e.target.value || undefined);
              }}
            />
            <Input
              type="date"
              aria-label="Emitidas até"
              className="w-40"
              value={filtros.emitidaAte ?? ''}
              onChange={(e) => {
                definir('emitidaAte', e.target.value || undefined);
              }}
            />
          </div>
        </div>
      }
      lista={
        <TabelaDados
          rotulo="Pendências"
          className="flex-1"
          colunas={colunasPendencias}
          dados={pendencias}
          idLinha={chave}
          carregando={lista.isPending}
          aoChegarAoFim={() => {
            if (lista.hasNextPage && !lista.isFetchingNextPage) void lista.fetchNextPage();
          }}
          vazio={
            <EstadoVazio
              icone={<ListChecks />}
              titulo={lista.isError ? 'A lista não carregou' : 'Nada pendente aqui'}
              texto={
                lista.isError
                  ? 'O servidor não respondeu. Recarregue a página em instantes.'
                  : 'Com estes filtros, não há nada a resolver.'
              }
              tom={lista.isError ? 'perigo' : 'neutro'}
            />
          }
        />
      }
    />
  );
}

function limparVazios<T extends Record<string, string | undefined>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
}
